import React, { useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../SectionHeading';
import { timeline } from '../../data/site';
export function Timeline() {
  const [active, setActive] = useState<number | null>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start 65%', 'end 60%']
  });
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    restDelta: 0.001
  });
  return <section id="experience" className="scroll-mt-24 bg-white py-24 lg:py-32" data-id="element-247" data-mp="3mrkg0slnfp8e">
      <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-248" data-mp="3rq57o3mu50l8">
        <SectionHeading eyebrow="Professional Experience" title="From NYSE counsel to Chief Legal Officer, 2012 to today." description="Select an organization to expand the mandate and the outcomes delivered." data-id="element-249" data-mp="jbddipeyzy17" />

        <div ref={containerRef} className="relative mt-16 pl-8 sm:pl-12" data-id="element-250" data-mp="26ie32qnnh67h">
          <div className="absolute bottom-0 left-[3px] top-2 w-px bg-navy/10 sm:left-[7px]" aria-hidden="true" data-id="element-251" data-mp="21jau4v4jufu8" />
          <motion.div aria-hidden="true" style={{
          scaleY,
          originY: 0
        }} className="absolute bottom-0 left-[3px] top-2 w-px bg-gold sm:left-[7px]" data-id="element-252" data-mp="2g8h1x7hgdedq" />

          <ol className="space-y-4" data-id="element-253" data-mp="2p72i4vzgchbc">
            {timeline.map((entry, index) => {
            const isOpen = active === index;
            return <Reveal as="li" key={entry.company} delay={0.04} data-id="element-254" data-mp="2kbdi6rjisyei" data-mpi={index}>
                  <div className="relative" data-id="element-255" data-mp="2z5rru1h72vgi">
                    <span className={`absolute -left-8 top-7 h-2 w-2 rotate-45 transition-all duration-500 sm:-left-12 ${isOpen ? 'scale-150 bg-gold' : 'bg-navy/25'}`} aria-hidden="true" data-id="element-256" data-mp="2u7ghlpmzmixr" />
                    <span className={`absolute -left-[1.55rem] top-8 hidden h-px transition-all duration-500 sm:block ${isOpen ? 'w-6 bg-gold' : 'w-4 bg-navy/15'}`} aria-hidden="true" data-id="element-257" data-mp="1phkyo3dhnebz" />

                    <div className={`group border transition-all duration-500 ${isOpen ? 'border-navy/15 bg-mist shadow-lux' : 'border-navy/10 bg-white hover:border-gold/50'}`} data-id="element-258" data-mp="23gt2j9kljami">
                      <h3 data-id="element-259" data-mp="2h87mbimbvfum">
                        <button type="button" onClick={() => setActive(isOpen ? null : index)} aria-expanded={isOpen} className="flex w-full items-start justify-between gap-6 px-6 py-6 text-left sm:px-8" data-id="element-260" data-mp="2h90r4e1q7va4">
                          <span className="min-w-0" data-id="element-261" data-mp="kienq4kkqza5">
                            <span className="eyebrow block text-[0.6rem] text-royal" data-id="element-262" data-mp="kj69yosa7knr">{entry.period}</span>
                            <span className="mt-2.5 block font-display text-xl font-normal text-navy sm:text-2xl" data-id="element-263" data-mp="fhqmyr6jn42q">
                              {entry.company}
                            </span>
                            <span className="mt-1.5 block text-sm text-charcoal/70" data-id="element-264" data-mp="5movhoosppwz">{entry.role}</span>
                            <span className="mt-2 block text-xs text-charcoal/50" data-id="element-265" data-mp="138cv0igjsocq">
                              {entry.locationDetail}
                            </span>
                          </span>
                          <span className={`mt-1 flex h-9 w-9 flex-none items-center justify-center rounded-full border transition-all duration-500 ${isOpen ? 'border-gold bg-gold text-navy' : 'border-navy/15 text-navy group-hover:border-gold group-hover:text-gold'}`} data-id="element-266" data-mp="th9srdbvu3bv">
                            {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-267" data-mp="16rbmstv6qs0r" /> : <PlusIcon className="h-4 w-4" data-id="element-268" data-mp="ru12cv0kebsw" />}
                          </span>
                        </button>
                      </h3>

                      <AnimatePresence initial={false} data-id="element-269" data-mp="1ki6qlrqtuixh">
                        {isOpen ? <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.45,
                      ease: [0.22, 1, 0.36, 1]
                    }} className="overflow-hidden" data-id="element-270" data-mp="1pf6upx449bly">
                            <div className="border-t border-navy/10 px-6 py-6 sm:px-8" data-id="element-271" data-mp="287rjb2cs8ujy">
                              <p className="text-sm italic text-charcoal/70" data-id="element-272" data-mp="3swd5qudzz3yb">{entry.summary}</p>
                              <ul className="mt-5 space-y-3.5" data-id="element-273" data-mp="3nytum3clj7al">
                                {entry.accomplishments.map(item => <li key={item} className="flex gap-3.5 text-sm leading-relaxed text-charcoal/80" data-id="element-274" data-mp="6gr3kymzy2g5">
                                    <span className="mt-2 h-1.5 w-1.5 flex-none rotate-45 bg-gold" aria-hidden="true" data-id="element-275" data-mp="1kk4npt0pr7p" />
                                    {item}
                                  </li>)}
                              </ul>
                            </div>
                          </motion.div> : null}
                      </AnimatePresence>
                    </div>
                  </div>
                </Reveal>;
          })}
          </ol>
        </div>
      </div>
    </section>;
}